import { IonChip, IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonProgressBar, IonRow, IonText, useIonViewWillEnter, useIonViewDidLeave, useIonViewWillLeave } from '@ionic/react';
import { arrowBack, arrowForward, checkmarkCircle, checkmarkCircleOutline, time } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useCountdownTimer } from '../components/CountdownTimer';
import { useAppContext } from '../AppProvider';
import { subMinutes, addDays, addMinutes, addHours, addWeeks, addSeconds } from 'date-fns';

interface ProgressBarProps {
    token: any;
    getTimer: any;
    status: any;
    setTimerStatus: any;
    startTimeISO: any;
    endTimeISO: any;
    alarmDelay: any;
    timerTimezone: any;
    todayIsDST: any;
    parent: any;
}

const TimerProgressBar: React.FC<ProgressBarProps> = ({token, getTimer, status, setTimerStatus, startTimeISO, endTimeISO, alarmDelay, timerTimezone, todayIsDST, parent}) => {
    const [isValid, setIsValid] = useState<boolean>(false);
    const [isPending, setIsPending] = useState<boolean>(false);
    const [isOverDue, setIsOverDue] = useState<boolean>(false);
    const [isAlert, setIsAlert] = useState<boolean>(false);
    const [duration, setDuration] = useState<string>('');
    const [pendingTime, setPendingTime] = useState<number>(0);
    const [pendingTimeStr, setPendingTimeStr] = useState<string>('');
    const [progress, setProgress] = useState<number>(0);
    const [progressTime, setProgressTime] = useState<number>(0);
    const [progressTimeStr, setProgressTimeStr] = useState<string>('');
    const [buffer, setBuffer] = useState<number>(1);
    const [remainingTime, setRemainingTime] = useState<number>(0);
    const [remainingTimeStr, setRemainingTimeStr] = useState<string>('');
    const [overDueTime, setOverDueTime] = useState<number>(0);
    const [overDueTimeStr, setOverDueTimeStr] = useState<string>('');
    const [alarmTime, setAlarmTime] = useState<number>(0);
    const [alarmTimeStr, setAlarmTimeStr] = useState<string>('');
    const [barColor, setBarColor] = useState<string>('success');
    const [secondsLeft, setSecondsLeft] = useState<number>(0);

    const { authorizedMember, loading, setLoading, getCurrentTimers, setCurrentTimer, setCurrentTimers, setCurrentTimerEnd, setCurrentTimerStatus } = useAppContext();
    
    const { subscribe, startCountdown, stopCountdown } = useCountdownTimer();
    
    const autoUpdateRef = useRef<NodeJS.Timeout | null>(null);

    useIonViewWillEnter(() => {   
        const enter = async () => {
            setLoading(true);
            await stopTimeout();
        };
        enter();
    });
   
    useEffect(() => {   
        if(autoUpdateRef.current !== null) {
            clearTimeout(autoUpdateRef.current);
            autoUpdateRef.current = null;
        }     
        if(startTimeISO && endTimeISO && timerTimezone) {
            if(parent == 'view' || parent == 'edit') { 
                if(status == '2') setBarColor('success');
                else if(status == '3') setBarColor('warning');
                else if(status == '4') setBarColor('danger');
                else setBarColor('success');

                //console.log(startTimeISO, endTimeISO, timerTimezone, todayIsDST);
            
                calculateDuration();
            }
        }
    }, [parent, secondsLeft, startTimeISO, endTimeISO, timerTimezone, status, isOverDue]);


    useEffect(() => {
        const unsub = subscribe(setSecondsLeft);
        return () => unsub();
    }, [subscribe]);
    

    useIonViewWillLeave(() => {
        const leave = async () => {
            await stopTimeout();
        };
        leave();
    });


    const stopTimeout = async () => {
        if(autoUpdateRef.current !== null) {
            clearTimeout(autoUpdateRef.current);
            autoUpdateRef.current = null;
        }
    };


    //
    // Parse time periods
    //
    const parseTimePeriod = async (periodSecs: number, displaySecs: boolean, round: string) => {
        const oneMinute = 60; // seconds
        const oneHour   = 60 * oneMinute;
        const oneDay    = 24 * oneHour;
        const oneWeek   = 7 * oneDay;

        let periodStr = '';
        let periodWeeksRem = 0;
        let periodDaysRem = 0;
        let periodHoursRem = 0;
        let periodMinsRem = 0;

        if(periodSecs >= oneWeek) {
            let periodWeeksDec = periodSecs / oneWeek;
            let periodWeeksInt = Math.floor(periodWeeksDec);
            periodWeeksRem = (periodWeeksDec - periodWeeksInt) * oneWeek;
            periodStr = periodWeeksInt.toString() + ' wk';
            if(periodWeeksInt >= 2) periodStr += 's';
            periodSecs = Math.ceil(periodWeeksRem);
        }
        
        if(periodSecs >= oneDay) {
            let periodDaysDec = periodSecs / oneDay;
            let periodDaysInt = Math.floor(periodDaysDec);
            periodDaysRem = (periodDaysDec - periodDaysInt) * oneDay;
            periodStr = periodStr + ' ' + periodDaysInt.toString() + ' day';
            if(periodDaysInt >= 2) periodStr += 's';
            periodSecs = Math.ceil(periodDaysRem);
        }
        
        if(periodSecs >= oneHour) {
            let periodHoursDec = periodSecs / oneHour;
            let periodHoursInt = Math.floor(periodHoursDec);
            periodHoursRem = (periodHoursDec - periodHoursInt) * oneHour;
            periodStr = periodStr + ' ' + periodHoursInt.toString() + ' hr';
            if(periodHoursInt >= 2) periodStr += 's';
            periodSecs = Math.ceil(periodHoursRem);
        }
        
        if(periodSecs >= oneMinute) {
            let periodMinutesDec = periodSecs / oneMinute;            
            let periodMinutesInt = round == 'ceil' ? Math.ceil(periodMinutesDec) : Math.floor(periodMinutesDec);
            periodMinsRem = (periodMinutesDec - periodMinutesInt) * oneMinute;
            if(periodMinutesInt == 60) periodMinutesInt = 59;
            periodStr = periodStr + ' ' + periodMinutesInt.toString() + ' min';
            if(periodMinutesInt >= 2) periodStr += 's';
            periodSecs = Math.ceil(periodMinsRem);
        } else if(periodSecs < oneMinute && periodStr == '') {
            periodStr = periodStr + ' < 1 min';
        }

        if (displaySecs) {
            if(periodSecs >= 1) {
                periodStr = periodStr + ' ' + periodSecs.toString() + ' sec';
            } else if(periodSecs == 0) {
                periodStr = periodStr + ' 00 sec'
            }
        }

        return periodStr;
    };


    function getTimezoneOffsetMinutes(
        timeZone: string,
        date: Date = new Date()
    ): number {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone,
            timeZoneName: "longOffset"
        }).formatToParts(date);

        const offsetPart = parts.find(part => part.type === "timeZoneName")?.value;

        if (!offsetPart || offsetPart === "GMT") {
            return 0;
        }

        const match = offsetPart.match(/^GMT([+-])(\d{2}):(\d{2})$/);

        if (!match) {
            throw new Error(`Unable to determine offset for ${timeZone}`);
        }

        const sign = match[1] === "+" ? 1 : -1;
        const hours = Number(match[2]);
        const minutes = Number(match[3]);

        return sign * (hours * 60 + minutes);
    }

    
    //
    // Calculate the Timer Duration
    //
    const calculateDuration = async () => {

        if (startTimeISO === '' || endTimeISO === '') return;

        await stopTimeout();
        
        const NowDateUTC = new Date();

        const localTZOffset = NowDateUTC.getTimezoneOffset();
        const timerTZOffset = getTimezoneOffsetMinutes(timerTimezone);

        const NowDateLocal = subMinutes(NowDateUTC, localTZOffset);
        const NowDateTimer = addMinutes(NowDateUTC, timerTZOffset);
      
        let nowTimeLocal = NowDateLocal.getTime();
            nowTimeLocal = Math.round(nowTimeLocal / 1000); 

        let nowISOLocal = NowDateLocal.toISOString();
            nowISOLocal = nowISOLocal.slice(0, -5);

        let nowTimeTimer = NowDateTimer.getTime();
            nowTimeTimer = Math.round(nowTimeTimer / 1000); 
            
        let nowISOTimer = NowDateTimer.toISOString();
            nowISOTimer = nowISOTimer.slice(0, -5);        
        
        const StartDateUTC = new Date(startTimeISO);
        const StartDateLocal = subMinutes(StartDateUTC, localTZOffset);
        const StartDateTimer = addMinutes(StartDateUTC, timerTZOffset);
        
        let startTimeLocal = StartDateLocal.getTime();
            startTimeLocal = Math.round(startTimeLocal / 1000);  

        let startTimeTimer = StartDateTimer.getTime();
            startTimeTimer = Math.round(startTimeTimer / 1000);

        const EndDateUTC = new Date(endTimeISO);
        const EndDateTimer = addMinutes(EndDateUTC, timerTZOffset);

        let endTime = EndDateTimer.getTime();
            endTime = Math.round(endTime / 1000);  
            
        //console.log(nowISOLocal, nowISOTimer, startTimeISO, nowTimeLocal, nowTimeTimer, startTimeTimer, endTime);

        if (startTimeTimer >= endTime) return;

        let totalTime = 0;
        let totalTimeStr = '';

        let beforeTime = 0;
        let beforeTimeStr = '';

        let elapsedTime = 0;
        let elapsedTimeStr = '';

        let remainingTime = 0;
        let remainingTimeStr = '';

        let overdueTime = 0;
        let overdueTimeStr = '';

        let alarmTime = 0;
        let alarmTimeStr = '';

        // Total time
        totalTime = endTime - startTimeTimer;

        totalTimeStr = await parseTimePeriod(totalTime, false, 'floor');
        totalTimeStr = totalTimeStr + ' Total';
        setDuration(totalTimeStr);
        setIsValid(totalTime > 0);

        // Before time
        beforeTime = startTimeTimer - nowTimeTimer;
        beforeTimeStr = await parseTimePeriod(beforeTime, false, 'ceil');
        beforeTimeStr = beforeTimeStr + ' Until Start';
        setPendingTimeStr(beforeTimeStr);
        if (beforeTime > 0) {
            setIsPending(true);
            setIsOverDue(false);
            setBuffer(1);
            setProgress(0); 
            setPendingTime(beforeTime); 
        } else if (beforeTime < 0) {
            
            if (status == '0' && parent == 'view') {
                //console.log('pending -> active');
                await stopTimeout();

                const T = await getTimer(token);
                const Ts = await getCurrentTimers(authorizedMember.memberToken);
                
                if (T !== undefined) {
                    setCurrentTimer(T);
                    setCurrentTimers(Ts);
                    
                    if (endTime) setCurrentTimerEnd(endTime);
                    setTimerStatus('1');
                    setCurrentTimerStatus('1');
                    setBarColor('success');
                    setIsPending(false);
                    setBuffer(0);                

                    await calculateDuration();
                }
            }
            setIsPending(false);
        }

        // Elapsed time
        elapsedTime = nowTimeTimer - startTimeTimer;
        elapsedTimeStr = await parseTimePeriod(elapsedTime, false, 'floor');
        elapsedTimeStr = elapsedTimeStr + ' Elapsed';
        setProgressTimeStr(elapsedTimeStr);
        if (elapsedTime > 0 && beforeTime < 0) {
            let progressDec = elapsedTime / totalTime; 
            if (progressDec > 1) progressDec = 1;
            setProgress(progressDec); 
            setBuffer(0);
            setIsPending(false);
        }

        // Remaining time (could be negative or positive for a current Timer)
        remainingTime = endTime - nowTimeTimer;
        remainingTimeStr = await parseTimePeriod(remainingTime, false, 'ceil');
        remainingTimeStr = remainingTimeStr + ' Remaining';
        setRemainingTimeStr(remainingTimeStr);

        // Overdue time
        overdueTime = nowTimeTimer - endTime;
        overdueTimeStr = await parseTimePeriod(overdueTime, false, 'floor');
        overdueTimeStr = overdueTimeStr + ' Over-due';
        setOverDueTime(overdueTime);
        setOverDueTimeStr(overdueTimeStr);
        if (overdueTime > 0 && status != '2') {
            setIsPending(false);
            setIsOverDue(true);
            setTimerStatus('3');
            setCurrentTimerStatus('3');
            setBarColor('warning');
        }

        // Alarm time
        alarmTime = nowTimeTimer - (endTime + (alarmDelay * 60 * 60));
        alarmTimeStr = await parseTimePeriod(alarmTime, false, 'floor');
        alarmTimeStr = alarmTimeStr + ' Over-due';
        setAlarmTime(alarmTime);
        setAlarmTimeStr(alarmTimeStr);
        if (alarmTime > 0 && status != '2') {
            setIsPending(false);
            setIsAlert(true);
            setTimerStatus('4');
            setCurrentTimerStatus('4');
            setBarColor('danger');
        }

        

        if (status == '0' && secondsLeft === 0 && autoUpdateRef.current === null) {
            let refreshTime = Math.abs(beforeTime) <= 10 ? 1000 : 10000;            
            autoUpdateRef.current = setTimeout(() => calculateDuration(), refreshTime); 
        }     
        
        if(status == '1' && autoUpdateRef.current !== null) {
            await stopTimeout();
        }

        setLoading(false);

    };


    return (
        <>
            {/*{isValid &&
                <IonChip color={'primary'}><strong><IonIcon icon={time} slot='start'></IonIcon> {duration}</strong></IonChip>
            }
            {!isValid &&
                <IonText color={'danger'}><strong><IonIcon icon={arrowUpCircleOutline}></IonIcon> Invalid Time Selection: The Check-in Time must be later than the Starting Time</strong></IonText>
            }*/}
                           
            <div className='ion-text-center'>
                <h1><IonText color={'tertiary'}><strong>{duration}</strong></IonText></h1>
            </div>
            
            {/*status {barColor} {buffer} {progress}*/}
            <IonProgressBar buffer={(parent == 'create' || status == '2') ? 1 : buffer} className='ion-margin-bottom ion-margin-top' color={status == '2' ? 'success' : barColor} value={status == '2' ? 1 : progress}></IonProgressBar>            
            
            <IonGrid>
                {(isPending && status != '2') &&
                    (<IonRow>
                        <IonCol sizeXs='12'>
                            <div className='ion-text-center'>                
                                <IonChip color={'medium'}>{pendingTimeStr}</IonChip>
                            </div>
                        </IonCol>
                    </IonRow>)
                }

                {(!isPending && status != '2' && !loading && progressTimeStr != '') &&
                    (<IonRow>
                        <IonCol sizeXs='12' sizeSm='6'>
                            <div className=' ion-text-center'>                
                                <p><IonChip color={'success'}>{progressTimeStr}</IonChip></p>
                            </div>
                        </IonCol>
                        <IonCol sizeXs='12' sizeSm='6'>
                            <div className='ion-text-center'>   
                                <p className='ion-margin-auto'><IonChip color={status == '1' ? 'secondary' : barColor}>{overDueTime > 0 ? overDueTimeStr : remainingTimeStr}</IonChip></p>                
                            </div>
                        </IonCol>
                    </IonRow>)
                }

                {status == '2' &&
                    (<IonRow>
                        <IonCol sizeXs='12'>
                            <div className=' ion-text-center'>                
                                <p className='ion-margin-auto'><IonChip color={'success'}><strong>Checked-In</strong></IonChip></p> 
                            </div>
                        </IonCol>
                    </IonRow>)
                }
                
                {/*<div className=' ion-text-center'>  
                    {secondsLeft === 0 || secondsLeft === null 
                        ? "No active timer"
                        : `${secondsLeft}s`}
                </div>*/}           
                
            </IonGrid>            
        </>
    );
};

export default TimerProgressBar;